import { useEffect } from 'react';
import DataTable from '../../common/components/table/DataTable';
import useThunkDispatch from '../../common/hooks/useThunkDispatch';
import useTypedSelector from '../../common/hooks/useTypedSelector';
import { formatDateFromSeconds } from '../../common/services/date/formatDate';
import FetchStatus from '../../common/types/FetchStatus';
import { fetchRegisteredApps, RegisteredApp } from './RegistrationsSlice';
import ClientFacingStatus from '../../domain/enums/ClientFacingStatus';
import Actions from 'domain/components/application/Actions';
import RejectionStatusCd from 'domain/enums/RejectionStatusCd';
import RejectionTypeCd from 'domain/enums/RejectionTypeCd';
import { addToCart, removeFromCart } from 'features/cart/CartSlice';
import { GroupKeyLink } from 'domain/components/application/GroupKeyLink';

function MyRegistrationTable(props: { hideOnEmpty: boolean }) {
  const { registeredApps } = useTypedSelector((state) => ({
    registeredApps: state.registeredApps,
  }));

  const { groupRefKeys } = useTypedSelector((state) => ({
    groupRefKeys: state.cart.groupRefKeys,
  }));
  function addAppToCart(id: string) {
    dispatch(addToCart(id));
  }

  const dispatch = useThunkDispatch();

  useEffect(() => {
    dispatch(
      fetchRegisteredApps({
        pageNum: registeredApps.meta.pagination.currentPage,
        pageSize: registeredApps.meta.pagination.itemsPerPage,
        sortBy: registeredApps.meta.sortBy,
        sortDir: registeredApps.meta.sortDir,
      })
    );
  }, [dispatch]);

  const handlePageChange = (page: number, size: number) => {
    dispatch(
      fetchRegisteredApps({
        pageNum: page,
        pageSize: size,
        sortBy: registeredApps.meta.sortBy,
        sortDir: registeredApps.meta.sortDir,
      })
    );
  };

  const handleSort = (sortBy: string, sortDir: string) => {
    dispatch(
      fetchRegisteredApps({
        pageNum: 0,
        pageSize: registeredApps.meta.pagination.itemsPerPage,
        sortBy: sortBy,
        sortDir: sortDir,
      })
    );
  };

  const isDeniedAndHasUnresolvedRejection = (
    app: RegisteredApp,
    typeCd: RejectionTypeCd
  ) =>
    app.statusCd === ClientFacingStatus.PRECHECK_FAILED &&
    app.rejectionReasons.some(
      (rr) =>
        rr.typeCd === typeCd && rr.statusCd === RejectionStatusCd.NOT_RESOLVED
    );

  if (props.hideOnEmpty && registeredApps.meta.currentPageIds.length == 0)
    return (
      <p>
        Each approved registration will receive a registration number. Every
        listing for each intermediary platform is required to have an approved
        registration number.
      </p>
    );

  return (
    <DataTable
      responsive
      data={registeredApps.meta.currentPageIds.map(
        (id) => registeredApps.entities[id]
      )}
      keyField='appId'
      pagination={{
        totalPages: registeredApps.meta.pagination.totalPages,
        totalCount: registeredApps.meta.pagination.totalCount,
        currentPage: registeredApps.meta.pagination.currentPage,
        itemsPerPage: registeredApps.meta.pagination.itemsPerPage,
        onPageChange: handlePageChange,
        loading: registeredApps.meta.fetchStatus === FetchStatus.loading,
      }}
      columns={[
        {
          name: 'Number',
          render: (row) => (
            <GroupKeyLink
              groupKey={row.groupRefKey}
              text={row.registrationNumber}
            />
          ),
        },
        {
          name: 'Address',
          render: (row) => (
            <div style={{ fontSize: '1.4rem' }}>{row.address}</div>
          ),
          onSort: (sortDir) => handleSort('ADDRESS', sortDir),
        },
        {
          name: 'Expiration Date',
          render: (row) => (
            <div>{formatDateFromSeconds(row.expirationDate)}</div>
          ),
        },
        {
          name: 'Status',
          render: (row) => <div>{row.statusDescr}</div>,
        },
        {
          name: 'Action Required',
          render: (data) => (
            <Actions
              rejections={data.rejectionReasons}
              shouldResubmit={
                data.statusCd === ClientFacingStatus.PRECHECK_FAILED &&
                data.rejectionReasons.every(
                  (rr) => rr.statusCd !== RejectionStatusCd.NOT_RESOLVED
                )
              }
              shouldReuploadID={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.IDENTITY
              )}
              shouldResuploadPrimaryResidence={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.PRIMARY_RESIDENCE
              )}
              shouldResuploadPrimaryResidence2={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.PRIMARY_RESIDENCE_02
              )}
              shouldSubmitShuol={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.SHUOL
              )}
              shouldCheckout={
                data.canCheckout && !groupRefKeys.includes(data.groupRefKey)
              }
              onCheckout={() => addAppToCart(data.groupRefKey)}
              isInCart={groupRefKeys.includes(data.groupRefKey)}
              onRemoveFromCart={() =>
                dispatch(removeFromCart(data.groupRefKey))
              }
              hasCityDebt={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.CITY_DEBT
              )}
              isRevoked={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.REVOKED
              )}
              groupRefKey={data.groupRefKey}
              isRegistrationAction={true}
              canRequestRenewal={
                data.statusCd === ClientFacingStatus.READY_FOR_RENEWAL
              }
            />
          ),
        },
      ]}
    />
  );
}

export default MyRegistrationTable;
